import { Link, useParams } from "react-router-dom";
import { MessageCircle, Phone, MapPin, BookOpen, ChevronLeft, ShieldCheck, Crosshair, Car, Ruler } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import NotFound from "@/pages/NotFound";
import ServiceIcon, { SERVICE_ICON_BY_SLUG } from "@/components/ServiceIcon";
import { PHONE_TEL, openWa } from "@/lib/wa";

export const KEYWORD_PAGES = {
  "tire-change-riyadh": {
    serviceSlug: "tire-change", serviceTitle: "تغيير كفرات",
    h1: "تغيير كفرات متنقل في الرياض",
    seoTitle: "تغيير كفرات متنقل الرياض | تغيير إطارات السيارات في موقعك | ميرو",
    seoDesc: "خدمة تغيير كفرات متنقلة في جميع أحياء الرياض: الفك والتركيب والترصيص وضبط الهواء في موقعك. أرسل مقاسك وحيك عبر واتساب 0507359999.",
    waService: "تغيير الإطارات",
    article: { slug: "when-to-change-tires", title: "متى يجب تغيير كفرات السيارة؟ 7 علامات مهمة" },
    sections: [
      { h: "كيف تتم عملية تغيير الكفرات في موقعك؟", p: "يصل الفني إلى حيك بمعدات الفك والتركيب والترصيص. تتم العملية كاملة حيث تتوقف سيارتك: فك الكفرات القديمة، تركيب الجديدة بعزم الشد الصحيح لسيارتك، ترصيص العجلات، ثم ضبط ضغط الهواء على القيم الموصى بها. لا تحتاج لقيادة السيارة لأي مكان." },
      { h: "ماذا تجهز قبل الطلب؟", p: "ثلاث معلومات تختصر العملية: مقاس الإطار المطبوع على جدار الكفر (مثل 205/55 R16)، نوع سيارتك وموديلها وسنة صنعها، وحيك في الرياض مع أقرب موقع واضح. إذا لم تعرف المقاس، صوّر جدار الكفر وأرسله في رسالة واتساب وسنقرأه معك." },
      { h: "هل التغيير المتنقل آمن لسيارتي؟", p: "نعم، العملية نفسها تُنفذ بمعايير الورشة: رفع آمن للسيارة، عزم شد صحيح، وترصيص بعد التركيب. الفرق الوحيد أن الورشة أتت إليك بدل أن تذهب أنت إليها، وبعد التأكد من توفر الفني والمعدات في حيك." },
    ],
    faq: [
      { q: "هل يشمل التغيير ترصيص العجلات؟", a: "نعم، يُنصح بالترصيص مع كل تركيب كفرات جديدة ويُنفذ في موقعك ضمن العملية." },
      { q: "هل أحضر الكفرات بنفسي؟", a: "أرسل مقاسك وحيك عبر واتساب ونتحقق من توفر الإطارات المناسبة ونرتب معك التفاصيل قبل الموعد." },
    ],
  },
  "puncture-riyadh": {
    serviceSlug: "puncture-repair", serviceTitle: "إصلاح بنشر",
    h1: "إصلاح بنشر متنقل في الرياض",
    seoTitle: "إصلاح بنشر متنقل الرياض | تصليح كفرات في موقعك | ميرو",
    seoDesc: "بنشر في الرياض؟ يصلك فني ميرو لموقعك لفحص الثقب وإصلاحه بالطريقة الصحيحة إذا كان قابلاً للإصلاح. واتساب 0507359999 مع مشاركة موقعك.",
    waService: "إصلاح البنشر",
    article: { slug: "repair-or-replace", title: "هل يمكن إصلاح الكفر المثقوب أم يجب استبداله؟" },
    sections: [
      { h: "لماذا لا تقُد سيارتك على بنشر؟", p: "القيادة على كفر منثور — ولو مسافة قصيرة — تكسر طبقات الإطار من الداخل وقد تحوله من إصلاح بسيط إلى استبدال كامل، إضافة لخطر فقدان التحكم. الخيار الأذكى: أوقف في مكان آمن واطلب فنياً يأتي إليك." },
      { h: "الإصلاح يبدأ بفحص لا بقرار", p: "يفحص الفني الثقب بعد فك الإطار: مكانه في المداس أم الجدار الجانبي، حجمه، وحالة الإطار الداخلية. الثقوب الصغيرة في منطقة المداس تُصلح بالرقعة الصحيحة، أما ضرر الجدار الجانبي أو الانتفاخ فنوضح لك أن الاستبدال هو الأأمن — ولا نُصلح ما لا يستوفي شروط السلامة." },
      { h: "أينما كنت في الرياض", p: "في البيت، العمل، أو الكتف الآمن لطريق داخلي — أرسل موقعك عبر واتساب بعد وصولك لمكان آمن وسنتحقق من تغطية حيك وموعد وصول الفني المتاح." },
    ],
    faq: [
      { q: "كم يستغرق إصلاح البنشر؟", a: "يعتمد على حالة الإطار بعد الفحص؛ الفني يشرح لك الوضع والخيار الصحيح قبل بدء العمل." },
      { q: "ماذا لو كان الضرر في الجدار الجانبي؟", a: "ضرر الجدار الجانبي لا يُصلح لأسباب تتعلق بالسلامة — سنشرح لك خيارات الاستبدال المتوفرة." },
    ],
  },
  "balancing-riyadh": {
    serviceSlug: "balancing", serviceTitle: "ترصيص كفرات",
    h1: "ترصيص كفرات متنقل في الرياض",
    seoTitle: "ترصيص كفرات متنقل الرياض | ترصيص في الموقع بأحدث الأجهزة | ميرو",
    seoDesc: "اهتزاز في المقود بعد سرعة معينة؟ ترصيص العجلات في موقعك في الرياض عند تغيير الإطارات أو علاج الاهتزاز. واتساب 0507359999.",
    waService: "ترصيص الإطارات",
    article: { slug: "balancing-vs-alignment", title: "الفرق بين ترصيص الكفرات وضبط زوايا العجلات" },
    sections: [
      { h: "متى تحتاج ترصيصاً فعلاً؟", p: "أشهر دليل: اهتزاز المقود في نطاق سرعة محدد ثم خفوت عند غيرها. وتُنفذ العملية دائماً عند تركيب كفرات جديدة، وبعد إصلاح بنشر كبير، ومع ظهور تآكل متعرج على المداس. تأجيله يُكلّف مخاملات التعليق وأطراف التوجيه." },
      { h: "الترصيص في موقعك بلا انتظار", p: "يصل الفني بجهاز ترصيص متنقل ويعمل على عجلاتك واحدة تلو الأخرى: فك، قياس اختلال التوزيع، تثبيت الأوزان الصحيحة على الجنط، وإعادة التركيب بعزم الشد الصحيح. النتيجة قيادة أنعم وعمر أطول لكفراتك." },
      { h: "الترصيص ليس علاجاً لكل شيء", p: "إذا كان المقود يسحب لجهة أو التآكل من طرف واحد، فالمشكلة في زوايا العجلات وليست في الترصيص — سنوجهك للفحص الصحيح بدل تنفيذ عملية لا تعالج السبب." },
    ],
    faq: [
      { q: "هل الترصيص ضروري مع كل تغيير كفرات؟", a: "يُنصح به دائماً عند التركيب؛ فالإطار الجديد بلا ترصيص قد يسبب اهتزازاً وتآكلاً مبكراً." },
      { q: "كيف أعرف أن المشكلة ترصيص أم زوايا؟", a: "اهتزاز في سرعات محددة يميل للترصيص، وسحب المقود أو تآكل طرف واحد للزوايا — الفني يفحص ويوجهك للصحيح." },
    ],
  },
  "battery-riyadh": {
    serviceSlug: "batteries", serviceTitle: "تغيير بطاريات",
    h1: "تغيير بطاريات متنقل في الرياض",
    seoTitle: "تغيير بطاريات سيارات متنقل الرياض | فحص واستبدال في موقعك | ميرو",
    seoDesc: "بطارية سيارتك ضعيفة أو توقفت؟ فحص واستبدال البطارية في موقعك بأي حي بالرياض، مع التأكد من نظام الشحن قبل الاستبدال. واتساب 0507359999.",
    waService: "تغيير البطاريات",
    article: { slug: "when-car-battery-needs-check", title: "متى تحتاج بطارية السيارة إلى الفحص أو التغيير؟" },
    sections: [
      { h: "حرارة الرياض تستنزف البطاريات", p: "الحر المرتفع يسرّع التلف الكيميائي داخل البطارية، لذا موسم الانقطاعات المفاجئة في الرياض يبدأ غالباً مع أشهر الصيف. علامات البداية: مارش أبطأ صباحاً، إضاءة أخفت من المعتاد، ونوافذ وشاشات تتباطأ." },
      { h: "نُفحص أولاً ثم نُبدّل إذا لزم", p: "قد لا تكون البطارية هي المشكلة: لمبة البطارية أثناء القيادة تعني غالباً خللاً في الدينامو أو نظام الشحن، وتركيب بطارية جديدة فوقه يعني انقطاعاً آخر بعد أيام. يفحص الفني الجهد والشحن ويوضح لك الحقيقة قبل أي استبدال." },
      { h: "الاستبدال في الموقع", p: "أرسل نوع سيارتك وموديلها وسنة صنعها وحيك عبر واتساب، ويصلك الفني بالبطارية ذات المواصفات المطابقة لتوصية الشركة المصنعة، ويعيد ضبط الإعدادات الأساسية بعد التركيب." },
    ],
    faq: [
      { q: "كيف أعرف أن المشكلة بالبطارية وليس الدينامو؟", a: "بالفحص: قياس الجهد وحالة الشحن يفرّق بين بطارية انتهت ونظام شحن معطوب — لذلك نبدأ بالفحص دائماً." },
      { q: "هل الخدمة متاحة في حيّي؟", a: "نغطي جميع أحياء الرياض بعد التأكيد — أرسل حيك عبر واتساب وسنخبرك فوراً." },
    ],
  },
  "tpms-riyadh": {
    serviceSlug: "tpms", serviceTitle: "برمجة حساسات الكفرات",
    h1: "برمجة حساسات الكفرات في الرياض",
    seoTitle: "برمجة حساسات الكفرات الرياض | فحص واستبدال TPMS في موقعك | ميرو",
    seoDesc: "لمبة ضغط الإطارات لا تهدأ؟ فحص وبرمجة واستبدال حساسات TPMS في موقعك بأي حي بالرياض وفق توافق الحساس مع سيارتك. واتساب 0507359999.",
    waService: "برمجة أو استبدال حساسات الكفرات",
    article: { slug: "how-tpms-works", title: "حساسات الكفرات TPMS: كيف تعمل ومتى تحتاج إلى البرمجة؟" },
    sections: [
      { h: "لماذا تظهر لمبة الضغط وتبقى؟", p: "أحياناً السبب نقص هواء حقيقي يكفي ضبطه، وأحياناً حساس انتهت بطاريته بعد سنوات من العمل أو تضرر أثناء خدمة كفرات. لمبة وامضة دقيقة ثم ثابتة هي إشارة النظام الكلاسيكية على خلل حساس — وتحتاج قراءة بجهاز مخصص لا تخميناً." },
      { h: "البرمجة والتوافق", p: "بعد استبدال حساس أو تركيب عجلات جديدة يجب تسجيل الحساسات في نظام السيارة لتظهر قراءاتها. التوافق شرط أساسي: ليست كل الحساسات مناسبة لكل سيارة، لذلك نطلب نوع سيارتك وموديلها وسنة صنعها قبل تأكيد أي خيار." },
      { h: "الخدمة في موقعك", p: "لا حاجة لسحب السيارة لورشة: يفحص الفني الحساسات في موقعك بجهاز القراءة، ويوضح لك إن كان الحل برمجة وإعادة تعريف أم استبدال حساس متوافق، ثم يُسجّل الحساس الجديد ويعيد ضغط الكفرات الصحيح." },
    ],
    faq: [
      { q: "هل جميع الحساسات متوافقة مع سيارتي؟", a: "لا — الترددات والبروتوكولات تختلف بين المصنّعين والموديلات. أرسل نوع سيارتك وموديلها وسنة صنعها لنتأكد من الخيار المتوافق." },
      { q: "لمبة الضغط ظهرت بعد صباح بارد، هل هو الحساس؟", a: "غالباً نقص هواء طبيعي مع برد الليل يكفي ضبطه بارداً. إن عادت اللمبة مع ضبط صحيح، فالفحص الفني هو الفاصل." },
    ],
  },
};

const ZONES_AR = { north: "شمال الرياض", northeast: "شمال شرق الرياض", northwest: "شمال غرب الرياض", east: "شرق الرياض", west: "غرب الرياض", central: "وسط الرياض", south: "جنوب الرياض", southeast: "جنوب شرق الرياض", southwest: "جنوب غرب الرياض" };

export const HOOD_KEYWORD_PAGES = {
  "mobile-yasmin": { hood: "al-yasmin", name: "الياسمين", zone: "north", loc: "يقع حي الياسمين في شمال الرياض" },
  "mobile-narjis": { hood: "al-narjis", name: "النرجس", zone: "north", loc: "يقع حي النرجس في شمال الرياض" },
  "mobile-olya": { hood: "al-olya", name: "العليا", zone: "central", loc: "يقع حي العليا في وسط الرياض" },
  "mobile-rawdah": { hood: "al-rawdah", name: "الروضة", zone: "east", loc: "يقع حي الروضة في شرق الرياض" },
  "mobile-hittin": { hood: "hittin", name: "حطين", zone: "northwest", loc: "يقع حي حطين في شمال غرب الرياض" },
  "mobile-malqa": { hood: "al-malqa", name: "الملقا", zone: "northwest", loc: "يقع حي الملقا في شمال غرب الرياض" },
  "mobile-qurtubah": { hood: "qurtubah", name: "قرطبة", zone: "northeast", loc: "يقع حي قرطبة في شمال شرق الرياض" },
  "mobile-rahmaniyah": { hood: "al-rahmaniyah", name: "الرحمانية", zone: "east", loc: "يقع حي الرحمانية في شرق الرياض" },
  "mobile-naseem": { hood: "al-naseem", name: "النسيم", zone: "east", loc: "يقع حي النسيم في شرق الرياض" },
  "mobile-shafa": { hood: "al-shafa", name: "الشفا", zone: "south", loc: "يقع حي الشفا في جنوب الرياض" },
  "mobile-suwaidi": { hood: "al-suwaydi", name: "السويدي", zone: "west", loc: "يقع حي السويدي في غرب الرياض" },
  "mobile-aziziyah": { hood: "al-aziziyah", name: "العزيزية", zone: "south", loc: "يقع حي العزيزية في جنوب الرياض" },
  "mobile-sahafah": { hood: "al-sahafah", name: "الصحافة", zone: "north", loc: "يقع حي الصحافة في شمال الرياض" },
  "mobile-wadi": { hood: "al-wadi", name: "الوادي", zone: "north", loc: "يقع حي الوادي في شمال الرياض" },
  "mobile-arid": { hood: "al-arid", name: "العارض", zone: "north", loc: "يقع حي العارض في شمال الرياض" },
  "mobile-rabia": { hood: "al-rabia", name: "الربيع", zone: "north", loc: "يقع حي الربيع في شمال الرياض" },
  "mobile-waha": { hood: "al-waha", name: "الواحة", zone: "north", loc: "يقع حي الواحة في شمال الرياض" },
  "mobile-maha": { hood: "al-maha", name: "المها", zone: "north", loc: "يقع حي المها في شمال الرياض" },
  "mobile-urqah": { hood: "urqah", name: "عرقة", zone: "northwest", loc: "يقع حي عرقة في شمال غرب الرياض" },
  "mobile-sahab": { hood: "al-sahab", name: "السحاب", zone: "northwest", loc: "يقع حي السحاب في شمال غرب الرياض" },
  "mobile-aqiq": { hood: "al-aqiq", name: "العقيق", zone: "northwest", loc: "يقع حي العقيق في شمال غرب الرياض" },
  "mobile-nakhil": { hood: "al-nakhil", name: "النخيل", zone: "northwest", loc: "يقع حي النخيل في شمال غرب الرياض" },
  "mobile-falah": { hood: "al-falah", name: "الفلاح", zone: "northeast", loc: "يقع حي الفلاح في شمال شرق الرياض" },
  "mobile-yarmuk": { hood: "al-yarmuk", name: "اليرموك", zone: "northeast", loc: "يقع حي اليرموك في شمال شرق الرياض" },
  "mobile-gharnatah": { hood: "gharnatah", name: "غرناطة", zone: "northeast", loc: "يقع حي غرناطة في شمال شرق الرياض" },
  "mobile-ishbiliyah": { hood: "ishbiliyah", name: "إشبيلية", zone: "northeast", loc: "يقع حي إشبيلية في شمال شرق الرياض" },
  "mobile-nada": { hood: "al-nada", name: "الندى", zone: "northeast", loc: "يقع حي الندى في شمال شرق الرياض" },
  "mobile-ramal": { hood: "al-ramal", name: "الرمال", zone: "east", loc: "يقع حي الرمال في شرق الرياض" },
  "mobile-munsiyah": { hood: "al-munsiyah", name: "المونسية", zone: "east", loc: "يقع حي المونسية في شرق الرياض" },
  "mobile-khalij": { hood: "al-khalij", name: "الخليج", zone: "east", loc: "يقع حي الخليج في شرق الرياض" },
  "mobile-sulaimaniyah": { hood: "al-sulaimaniyah", name: "السليمانية", zone: "central", loc: "يقع حي السليمانية في وسط الرياض" },
  "mobile-wurud": { hood: "al-wurud", name: "الورود", zone: "central", loc: "يقع حي الورود في وسط الرياض" },
  "mobile-murabba": { hood: "al-murabba", name: "المربع", zone: "central", loc: "يقع حي المربع في وسط الرياض" },
  "mobile-malaz": { hood: "al-malaz", name: "الملز", zone: "central", loc: "يقع حي الملز في وسط الرياض" },
  "mobile-dirah": { hood: "al-dirah", name: "الديرة", zone: "central", loc: "يقع حي الديرة في وسط الرياض" },
  "mobile-dhahrat-badiah": { hood: "dhahrat-al-badiah", name: "ظهرة البديعة", zone: "west", loc: "يقع حي ظهرة البديعة في غرب الرياض" },
  "mobile-arija": { hood: "al-arija", name: "العريجاء", zone: "west", loc: "يقع حي العريجاء في غرب الرياض" },
  "mobile-ghadir": { hood: "al-ghadir", name: "الغدير", zone: "west", loc: "يقع حي الغدير في غرب الرياض" },
  "mobile-shubra": { hood: "shubra", name: "شبرا", zone: "west", loc: "يقع حي شبرا في غرب الرياض" },
  "mobile-dar-baida": { hood: "dar-al-baida", name: "الدار البيضاء", zone: "south", loc: "يقع حي الدار البيضاء في جنوب الرياض" },
  "mobile-badr": { hood: "badr", name: "بدر", zone: "south", loc: "يقع حي بدر في جنوب الرياض" },
  "mobile-manfuhah": { hood: "al-manfuhah", name: "المنفوحة", zone: "south", loc: "يقع حي المنفوحة في جنوب الرياض" },
  "mobile-nimar": { hood: "nimar", name: "نمار", zone: "southeast", loc: "يقع حي نمار في جنوب شرق الرياض" },
  "mobile-maruw": { hood: "al-maruw", name: "المروة", zone: "southeast", loc: "يقع حي المروة في جنوب شرق الرياض" },
  "mobile-rayn": { hood: "al-rayn", name: "الرين", zone: "southeast", loc: "يقع حي الرين في جنوب شرق الرياض" },
  "mobile-shuhada": { hood: "al-shuhada", name: "الشهداء", zone: "southeast", loc: "يقع حي الشهداء في جنوب شرق الرياض" },
  "mobile-tuwaiq": { hood: "tuwaiq", name: "طويق", zone: "southwest", loc: "يقع حي طويق في جنوب غرب الرياض" },
  "mobile-qirwan": { hood: "qirwan", name: "قيروان", zone: "southwest", loc: "يقع حي قيروان في جنوب غرب الرياض" },
  "mobile-ud": { hood: "al-ud", name: "العود", zone: "southwest", loc: "يقع حي العود في جنوب غرب الرياض" },
};

const SERVICE_KEYS = ["tire-change", "puncture-repair", "tpms", "balancing", "batteries"];
const SERVICE_NAMES = { "tire-change": "تغيير الإطارات", "puncture-repair": "إصلاح البنشر", tpms: "برمجة أو استبدال حساسات الكفرات", balancing: "ترصيص الإطارات", batteries: "تغيير البطاريات" };

function HoodKeywordPage({ kw, cfg }) {
  const others = Object.entries(HOOD_KEYWORD_PAGES).filter(([k]) => k !== kw).slice(0, 6);
  const waMsg = `السلام عليكم، أرغب في طلب خدمة من ميرو للإطارات وخدمات السيارات. موقعي في حي ${cfg.name} بالرياض، والخدمة المطلوبة ____. هل الخدمة متاحة في موقعي؟ أرجو إفادتي بالتكلفة.`;
  const hoodWa = (service) => `السلام عليكم، أرغب في طلب خدمة من ميرو للإطارات وخدمات السيارات. موقعي في حي ${cfg.name} بالرياض، والخدمة المطلوبة ${service}. هل الخدمة متاحة في موقعي؟ أرجو إفادتي بالتكلفة.`;

  return (
    <main>
      <SEO title={`بنشر متنقل في ${cfg.name} | تغيير كفرات وحساسات وبطاريات في موقعك | ميرو`} description={`خدمة البنشر المتنقل في حي ${cfg.name} بالرياض: تغيير كفرات، إصلاح بنشر، برمجة حساسات الكفرات، ترصيص، وتغيير بطاريات — أرسل موقعك عبر واتساب 0507359999 للتأكد من التغطية.`} path={`/${kw}`} jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", name: `هل تغطي ميرو حي ${cfg.name}؟`, "acceptedAnswer": { "@type": "Answer", text: "التغطية متاحة بعد التأكيد — أرسل موقعك عبر واتساب 0507359999 وسنتحقق فوراً من إمكانية تقديم الخدمة في موقعك." } },
          { "@type": "Question", name: `كيف أطلب بنشر متنقل في ${cfg.name}؟`, "acceptedAnswer": { "@type": "Answer", text: "راسلنا عبر واتساب على 0507359999 مع ذكر حيك ونوع سيارتك ووصف المشكلة، أو استخدم صفحة طلب الخدمة في الموقع لتجهيز رسالة منسقة." } },
        ],
      }} />

      <section className="relative overflow-hidden pb-14 pt-36 sm:pt-44">
        <div className="pointer-events-none absolute -top-32 start-1/4 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[140px]" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
          <Link to="/riyadh" className="mb-6 inline-flex items-center gap-1.5 text-sm text-mist transition-colors hover:text-gold" data-testid={`hoodkw-back-${kw}`}>
            <ChevronLeft size={15} className="rotate-180" /> مناطق التغطية
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <p className="eyebrow"><MapPin size={13} className="inline" /> {ZONES_AR[cfg.zone]}</p>
            <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold text-gold" data-testid={`hoodkw-status-${kw}`}>التغطية متاحة بعد التأكيد</span>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-[1.4] text-white sm:text-4xl lg:text-5xl" data-testid={`hoodkw-title-${kw}`}>بنشر متنقل في {cfg.name}</h1>
          <p className="mt-5 max-w-2xl text-base leading-9 text-mist">{cfg.loc} — تغيير كفرات، إصلاح بنشر، برمجة حساسات، ترصيص، وتغيير بطاريات يصلك إلى موقعك. أرسل موقعك عبر واتساب لنتحقق من إمكانية تقديم الخدمة في موقعك وموعد الفني المتاح.</p>
          <div className="mt-8 flex flex-wrap gap-3.5">
            <button onClick={() => openWa(waMsg)} className="btn-wa" data-testid={`hoodkw-wa-${kw}`}><MessageCircle size={18} /> اطلب في {cfg.name} عبر واتساب</button>
            <a href={PHONE_TEL} className="btn-gold" data-testid={`hoodkw-call-${kw}`}><Phone size={18} /> 0507359999</a>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-coal/40 py-14">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <h2 className="mb-6 text-2xl font-bold text-white">ماذا تجهز عند طلب الخدمة في {cfg.name}؟</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: Car, h: "بيانات سيارتك", p: "النوع والموديل وسنة الصنع — تحدد المعدات والقيم الصحيحة." },
                { icon: Ruler, h: "مقاس الإطار", p: "اقرأه من جدار الكفر (مثل 205/55 R16) أو صوّره وأرسله في الرسالة." },
                { icon: Crosshair, h: "موقعك بدقة", p: "مشاركة الموقع عبر واتساب هي الأسرع لتحديد مكانك في الحي." },
              ].map((c, i) => (
                <div key={i} className="rounded-3xl border border-white/10 bg-ink/70 p-6">
                  <c.icon size={22} className="mb-3 text-gold" />
                  <h3 className="text-sm font-bold text-white">{c.h}</h3>
                  <p className="mt-2 text-xs leading-6 text-mist">{c.p}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="mb-6 mt-12 text-2xl font-bold text-white">خدمات يمكن طلبها في {cfg.name}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {SERVICE_KEYS.map((s) => (
                <button key={s} onClick={() => openWa(hoodWa(SERVICE_NAMES[s]))} className="group flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-ink/70 p-5 text-center transition-all hover:-translate-y-1 hover:border-gold/40" data-testid={`hoodkw-service-${kw}-${s}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-gold/60 text-gold"><ServiceIcon icon={SERVICE_ICON_BY_SLUG[s]} size={20} /></span>
                  <span className="text-xs font-bold leading-5 text-white group-hover:text-gold">{SERVICE_NAMES[s]}</span>
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="mb-4 mt-12 text-2xl font-bold text-white">أسئلة يتكرر سؤالها في {cfg.name}</h2>
            {[
              { q: `هل تغطي ميرو حي ${cfg.name}؟`, a: "التغطية متاحة بعد التأكيد: أرسل موقعك عبر واتساب 0507359999 وسنتحقق فوراً من إمكانية الوصول وموعد الفني المتاح." },
              { q: `كم من الوقت يحتاج الفني ليصل إلى ${cfg.name}؟`, a: "يعتمد على التغطية اللحظية وترتيب الطلبات — نؤكد لك الموعد المتاح في المحادثة قبل التنفيذ، ولا نعد بوقت وصول نحن غير قادرين على الوفاء به." },
            ].map((f, i) => (
              <div key={i} className="mb-3 rounded-2xl border border-white/10 bg-ink/70 p-5" data-testid={`hoodkw-faq-${kw}-${i}`}>
                <h3 className="text-sm font-bold text-gold">{f.q}</h3>
                <p className="mt-2 text-sm leading-7 text-mist">{f.a}</p>
              </div>
            ))}
          </Reveal>

          <Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <Link to={`/neighborhood/${cfg.hood}`} className="group rounded-2xl border border-white/10 bg-coal/60 p-6 transition-all hover:border-gold/40" data-testid={`hoodkw-hoodpage-${kw}`}>
                <ShieldCheck size={18} className="mb-2 text-gold" />
                <h3 className="text-sm font-bold text-white group-hover:text-gold">صفحة حي {cfg.name} في خريطة التغطية</h3>
                <p className="mt-2 text-xs leading-6 text-dim">حالة التغطية المحدثة والأحياء القريبة</p>
              </Link>
              <Link to="/riyadh" className="group rounded-2xl border border-white/10 bg-coal/60 p-6 transition-all hover:border-gold/40" data-testid={`hoodkw-coverage-${kw}`}>
                <MapPin size={18} className="mb-2 text-gold" />
                <h3 className="text-sm font-bold text-white group-hover:text-gold">جميع أحياء الرياض</h3>
                <p className="mt-2 text-xs leading-6 text-dim">ابحث عن أي حي واستفسر عن توفر الخدمة فيه</p>
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-10 rounded-3xl border border-gold/20 bg-coal/50 p-6">
              <h3 className="mb-4 text-sm font-bold text-white">أحياء قريبة نخدمها أيضاً</h3>
              <div className="flex flex-wrap gap-2.5">
                {others.map(([k, c]) => (
                  <Link key={k} to={`/${k}`} className="rounded-full border border-white/12 bg-ink/70 px-4 py-2 text-sm text-zinc-300 transition-all hover:border-gold/50 hover:text-gold" data-testid={`hoodkw-nearby-${kw}-${k}`}>بنشر متنقل {c.name}</Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default function KeywordPage() {
  const { kw } = useParams();

  if (HOOD_KEYWORD_PAGES[kw]) return <HoodKeywordPage kw={kw} cfg={HOOD_KEYWORD_PAGES[kw]} />;

  const page = KEYWORD_PAGES[kw];
  if (!page) return <NotFound />;

  const waMsg = `السلام عليكم، أرغب في طلب خدمة ${page.waService} من ميرو للإطارات وخدمات السيارات.\nالحي: ____.\nنوع السيارة: ____.\nأرجو توضيح التوفر والتكلفة.`;

  return (
    <main>
      <SEO title={page.seoTitle} description={page.seoDesc} path={`/${kw}`} jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": page.faq.map((f) => ({
          "@type": "Question", name: f.q,
          "acceptedAnswer": { "@type": "Answer", text: f.a },
        })),
      }} />

      <section className="relative overflow-hidden pb-16 pt-36 sm:pt-44">
        <div className="pointer-events-none absolute -top-32 start-1/4 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[140px]" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-4"><MapPin size={13} className="inline" /> خدمة متنقلة — الرياض</p>
            <h1 className="text-3xl font-extrabold leading-[1.4] text-white sm:text-4xl lg:text-5xl" data-testid="kw-page-title">{page.h1}</h1>
            <p className="mt-5 max-w-2xl text-base leading-9 text-mist sm:text-lg">{page.seoDesc}</p>
            <div className="mt-8 flex flex-wrap gap-3.5">
              <button onClick={() => openWa(waMsg)} className="btn-wa" data-testid={`kw-wa-${kw}`}><MessageCircle size={18} /> اطلب عبر واتساب</button>
              <a href={PHONE_TEL} className="btn-gold" data-testid={`kw-call-${kw}`}><Phone size={18} /> 0507359999</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-coal/40 py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {page.sections.map((s, i) => (
            <Reveal key={i}>
              <h2 className="mb-4 mt-8 text-xl font-bold text-white sm:text-2xl" data-testid={`kw-section-${i}`}>{s.h}</h2>
              <p className="text-[15px] leading-9 text-zinc-300 sm:text-base">{s.p}</p>
            </Reveal>
          ))}

          <Reveal>
            <h2 className="mb-4 mt-12 text-xl font-bold text-white sm:text-2xl">أسئلة يتكرر سؤالها</h2>
            {page.faq.map((f, i) => (
              <div key={i} className="mb-3 rounded-2xl border border-white/10 bg-ink/70 p-5" data-testid={`kw-faq-${i}`}>
                <h3 className="text-sm font-bold text-gold">{f.q}</h3>
                <p className="mt-2 text-sm leading-7 text-mist">{f.a}</p>
              </div>
            ))}
          </Reveal>

          <Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <Link to={page.serviceSlug === "tpms" ? "/tpms" : `/services/${page.serviceSlug}`} className="group rounded-2xl border border-white/10 bg-coal/60 p-6 transition-all hover:border-gold/40" data-testid={`kw-service-link-${kw}`}>
                <BookOpen size={18} className="mb-2 text-gold" />
                <h3 className="text-sm font-bold text-white group-hover:text-gold">صفحة خدمة {page.serviceTitle}</h3>
                <p className="mt-2 text-xs leading-6 text-dim">كل تفاصيل الخدمة وحالات الحاجة إليها</p>
              </Link>
              <Link to={`/blog/${page.article.slug}`} className="group rounded-2xl border border-white/10 bg-coal/60 p-6 transition-all hover:border-gold/40" data-testid={`kw-article-link-${kw}`}>
                <BookOpen size={18} className="mb-2 text-gold" />
                <h3 className="text-sm font-bold text-white group-hover:text-gold">{page.article.title}</h3>
                <p className="mt-2 text-xs leading-6 text-dim">من مدونة ميرو</p>
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-gold/25 bg-coal/60 p-8 text-center">
              <p className="flex items-center gap-2 text-sm text-zinc-300"><MapPin size={16} className="text-gold" /> نغطي جميع أحياء الرياض بعد التأكيد — ابحث عن حيك في صفحة التغطية</p>
              <Link to="/riyadh" className="btn-ghost" data-testid={`kw-coverage-${kw}`}>مناطق التغطية</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
